import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { API_BASE } from '../config';

/* ─────────────────────────────────────────────────────────────
   COLOR PALETTE
───────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F7FF",
  softBlue: "#EBF1FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#0F1F3D",
  textMid: "#374966",
  textMuted: "#6B7FA3",
  border: "#D6E4FF",
  borderLight: "#EBF1FF",
  shadow: "0 4px 24px rgba(26,86,219,0.08)",
  shadowMd: "0 8px 40px rgba(26,86,219,0.12)",
  shadowLg: "0 20px 60px rgba(26,86,219,0.16)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EBF1FF 0%, #F4F7FF 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

const PaymentTracker = () => {
  const [payments, setPayments] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError("Please login first");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/payments`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Handle different possible response structures
      const paymentsArray = data.payments || data.data || data || [];
      setPayments(paymentsArray);

    } catch (err) {
      console.error("Payment fetch error:", err);
      setError(err.message || "Failed to load payments");
      setPayments([]);
      toast.error("Could not load payments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'successful': 
        return { bg: '#E8F5E9', text: '#4CAF50', border: '#4CAF50' };
      case 'pending': 
        return { bg: '#FEF3C7', text: T.accentGold, border: T.accentGold };
      case 'unsuccessful': 
        return { bg: '#FEE2E2', text: '#EF4444', border: '#EF4444' };
      case 'refunded': 
        return { bg: '#F3E8FF', text: '#9333EA', border: '#9333EA' };
      default: 
        return { bg: T.borderLight, text: T.textMuted, border: T.textMuted };
    }
  };

  const filteredPayments = filter === 'all' 
    ? payments 
    : payments.filter(p => p.status === filter);

  const stats = {
    total: payments.length,
    successful: payments.filter(p => p.status === 'successful').length,
    pending: payments.filter(p => p.status === 'pending').length,
    unsuccessful: payments.filter(p => p.status === 'unsuccessful').length,
    refunded: payments.filter(p => p.status === 'refunded').length,
    totalAmount: payments
      .filter(p => p.status === 'successful')
      .reduce((sum, p) => sum + (p.amount || 0), 0)
  };

  // Dummy data for demonstration if no payments exist
  const dummyPayments = [
    { _id: 'PAY-001', userEmail: 'john@example.com', amount: 299, status: 'successful', createdAt: '2024-01-15T10:30:00Z' },
    { _id: 'PAY-002', userEmail: 'jane@example.com', amount: 499, status: 'pending', createdAt: '2024-01-16T14:20:00Z' },
    { _id: 'PAY-003', userEmail: 'mike@example.com', amount: 199, status: 'successful', createdAt: '2024-01-14T09:15:00Z' },
    { _id: 'PAY-004', userEmail: 'sarah@example.com', amount: 399, status: 'unsuccessful', createdAt: '2024-01-13T16:45:00Z' },
    { _id: 'PAY-005', userEmail: 'alex@example.com', amount: 299, status: 'refunded', createdAt: '2024-01-12T11:00:00Z' },
  ];

  const displayPayments = payments.length > 0 ? payments : dummyPayments;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '256px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${T.accentGold}20`,
          borderTopColor: T.accentGold,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: T.white,
        padding: '32px',
        borderRadius: '16px',
        textAlign: 'center',
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`
      }}>
        <p style={{ color: '#EF4444', marginBottom: '16px' }}>⚠️ {error}</p>
        <button 
          onClick={fetchPayments}
          style={{
            padding: '12px 24px',
            background: T.gradPrimary,
            color: T.white,
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const displayFiltered = filter === 'all' 
    ? displayPayments 
    : displayPayments.filter(p => p.status === filter);

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{
            fontSize: '30px',
            fontWeight: 'bold',
            color: T.navy,
            marginBottom: '8px'
          }}>
            Payment Tracker
          </h1>
          <p style={{ color: T.textMuted }}>
            {stats.total} total payments • ${stats.totalAmount.toLocaleString()} revenue
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['all', 'successful', 'pending', 'unsuccessful', 'refunded'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                textTransform: 'capitalize',
                transition: 'all 0.2s',
                cursor: 'pointer',
                background: filter === status ? T.primary : T.white,
                color: filter === status ? T.white : T.textMid,
                border: filter === status ? 'none' : `1px solid ${T.border}`,
              }}
              onMouseEnter={(e) => {
                if (filter !== status) {
                  e.currentTarget.style.background = T.softBlue;
                }
              }}
              onMouseLeave={(e) => {
                if (filter !== status) {
                  e.currentTarget.style.background = T.white;
                }
              }}
            >
              {status}
              {status !== 'all' && (
                <span style={{ marginLeft: '4px', fontSize: '11px', opacity: 0.7 }}>
                  ({displayPayments.filter(p => p.status === status).length})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: T.white,
          padding: '16px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: T.shadow,
          border: `1px solid ${T.borderLight}`
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: T.navy }}>{stats.total}</div>
          <div style={{ fontSize: '13px', color: T.textMuted }}>Total</div>
        </div>
        <div style={{
          background: T.white,
          padding: '16px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: T.shadow,
          border: `1px solid ${T.borderLight}`
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#4CAF50' }}>{stats.successful}</div>
          <div style={{ fontSize: '13px', color: T.textMuted }}>Successful</div>
        </div>
        <div style={{
          background: T.white,
          padding: '16px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: T.shadow,
          border: `1px solid ${T.borderLight}`
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: T.accentGold }}>{stats.pending}</div>
          <div style={{ fontSize: '13px', color: T.textMuted }}>Pending</div>
        </div>
        <div style={{
          background: T.gradSoft,
          padding: '16px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: T.shadow,
          border: `1px solid ${T.borderLight}`
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: T.primary }}>${stats.totalAmount.toLocaleString()}</div>
          <div style={{ fontSize: '13px', color: T.textMuted }}>Revenue</div>
        </div>
      </div>

      {/* Payments Table */}
      <div style={{
        background: T.white,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}>
        {displayFiltered.length === 0 ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: T.textMuted
          }}>
            <p style={{ fontSize: '18px' }}>No payments found</p>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>Try changing the filter</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                  <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontSize: '13px', fontWeight: 600 }}>Payment ID</th>
                  <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontSize: '13px', fontWeight: 600 }}>User</th>
                  <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontSize: '13px', fontWeight: 600 }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontSize: '13px', fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontSize: '13px', fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '16px', color: T.textMuted, fontSize: '13px', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayFiltered.map((payment, index) => {
                  const statusColors = getStatusColor(payment.status);
                  return (
                    <motion.tr
                      key={payment._id || index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      style={{
                        borderBottom: `1px solid ${T.borderLight}`,
                        transition: 'background 0.2s ease',
                      }}
                      whileHover={{ backgroundColor: T.offWhite }}
                    >
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          color: T.text,
                          fontFamily: 'monospace',
                          fontSize: '13px'
                        }}>
                          {payment.paymentId || payment._id}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: T.gradPrimary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <span style={{
                              color: T.white,
                              fontSize: '12px',
                              fontWeight: 600
                            }}>
                              {payment.userEmail?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <span style={{ color: T.textMid, fontSize: '13px' }}>{payment.userEmail}</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          color: T.navy,
                          fontWeight: 600,
                          fontSize: '14px'
                        }}>
                          ${(payment.amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{ color: T.textMuted, fontSize: '13px' }}>
                          {payment.createdAt || payment.date 
                            ? new Date(payment.createdAt || payment.date).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric'
                              }) 
                            : 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          background: statusColors.bg,
                          color: statusColors.text,
                          border: `1px solid ${statusColors.border}40`,
                        }}>
                          {payment.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                              padding: '6px 12px',
                              background: T.softBlue,
                              color: T.primary,
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: 500,
                              cursor: 'pointer',
                            }}
                          >
                            View
                          </motion.button>
                          {payment.status === 'successful' && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              style={{
                                padding: '6px 12px',
                                background: '#FEE2E2',
                                color: '#EF4444',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: 500,
                                cursor: 'pointer',
                              }}
                            >
                              Refund
                            </motion.button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentTracker;